/* eslint-disable @typescript-eslint/no-use-before-define */
import REGULARS from 'components/UI_Kit/utils/regulars';
import { FormikHelpers, useFormik } from 'formik';
import { useAppSelector, useUserThunkDispatch } from 'hooks';
import useAsyncFetch from 'hooks/async-fetch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userActions } from 'store/user';
import { IChangeSelfPasswordBody } from 'store/user/types/api-bodies.types';
import { IChangePasswordForm } from 'store/user/types/forms.types';
import { ReactComponent as ArrowLeft } from 'assets/icons_3/arrows/arrow-left.svg';
import * as yup from 'yup';
import { AUTH_ERROR_CODES } from 'components/Auth/LoginForm';
import axios from 'axios';
import { IResponse } from 'services/api/axios';
import { UserStatus } from 'store/user/types/state.types';
import { User } from 'store/user/types';
import FormText from 'components/UI_kit_3/FormText/FormText';
import Button from 'components/UI_kit_3/Button/Button';
import ValidationCheck from 'components/UI_kit_3/ValidationCheck/ValidationCheck';
import classNames from 'classnames';
import styles from './ResetPasswordForm.module.scss';

const ResetPasswordForm = () => {
  const [t, i18n] = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const thunkUserDispatch = useUserThunkDispatch();
  const user = useAppSelector((state) => state.user);

  const handleLogOut = () => {
    dispatch(userActions.clearUser());
    navigate('/');
  };

  const [handleChangePass, loading] = useAsyncFetch<[IChangePasswordForm,
    FormikHelpers<IChangePasswordForm>], void>({
    async asyncFunc(form: IChangePasswordForm) {
      const body: IChangeSelfPasswordBody = {
        oldPassword: user.tempPass as string,
        newPassword: form.newPass,
        login: user.tempLogin as string,
      };
      const res = await thunkUserDispatch(userActions.changePassword(body));
      await thunkUserDispatch(userActions.signIn({
        login: user.tempLogin as string,
        password: form.newPass,
      }, i18n.language));

      return res;
    },
    errorHandler({ error, errMessage, params: [dto, helpers] }) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define

      if (axios.isAxiosError(error)) {
        const errors = error.response?.data?.errors as IResponse['errors'];

        const maintenanceError = errors
          ?.find((_error) => _error.code === AUTH_ERROR_CODES.MAINTENANCE);
        const endDate = maintenanceError?.message.split(' ').pop() || null;

        const twoFaError = errors
          ?.find((_error) => _error.code === AUTH_ERROR_CODES.TWO_FA_REQUIRED);

        if (maintenanceError) {
          const newUser = new User();
          user.status = UserStatus.Maintenance;
          user.maintenanceEndDate = endDate;
          dispatch(userActions.setUser(newUser));
          dispatch(userActions.setLoading(false));
          navigate('/maintenance', { replace: true });
          return;
        }
        if (twoFaError) {
          const newUser = new User();
          newUser.status = UserStatus.New;
          newUser.state = '2fa';
          newUser.tempLogin = user.tempLogin as string;
          newUser.tempPass = dto.newPass;
          dispatch(userActions.setUser(newUser));
          dispatch(userActions.setLoading(false));
          return;
        }
        helpers.setFieldError('newPassConfirm', errMessage);
        return;
      }
      helpers.setFieldError('newPassConfirm', errMessage);
    },

  });

  const schema = React.useMemo(() => yup.object().shape({
    newPass: yup.string()
      .required(t('form.error.required'))
      .min(12, t('form.error.password_weak'))
      .matches(REGULARS.password, t('form.error.password_weak'))
      .test('not_old_pass', t('form.error.pass_must_be_new'), (value) => value !== user.tempPass),
    newPassConfirm: yup.string().required(t('form.error.required')).oneOf([yup.ref('newPass'), null], t('form.error.confirm')),
  }), [t]);

  const initialForm = React.useMemo<IChangePasswordForm>(() => ({
    newPass: '',
    newPassConfirm: '',
  }), []);

  const {
    handleSubmit,
    handleChange,
    values,
    touched,
    errors,
  } = useFormik({
    validationSchema: schema,
    onSubmit: handleChangePass,
    initialValues: initialForm,
    validateOnChange: false,
  });

  const passwordValidation = {
    minCharacters: values.newPass.length >= 12,
    numbers: /\d/.test(values.newPass),
    capitals: /[A-Z]/.test(values.newPass),
    specSymbols: /[!@#$%^&*]/.test(values.newPass),

  };

  return (
    <form noValidate onSubmit={handleSubmit} id="account-form" autoComplete="off">
      <FormText
        className="mb-2xl"
        name="newPass"
        autoComplete="new-password"
        label={t('form.field.password')}
        value={values?.newPass}
        error={errors.newPass}
        type="password"
        isInvalid={touched.newPass && !!errors.newPass}
        onChange={handleChange}
        qaElement="new-password"
        qaElementFeedback="new-password-error"
      />
      <div className={classNames(styles.validation, 'mb-2xl')}>
        <ValidationCheck label={t('auth.password_requirements_min_12')} checked={passwordValidation.minCharacters} />
        <ValidationCheck label={t('auth.password_requirements_2')} checked={passwordValidation.numbers} />
        <ValidationCheck label={t('auth.password_requirements_3')} checked={passwordValidation.capitals} />
        <ValidationCheck label={t('auth.password_requirements_4')} checked={passwordValidation.specSymbols} />
      </div>
      <div className="mb-3xl">
        <FormText
          type="password"
          name="newPassConfirm"
          autoComplete="new-password"
          label={t('form.field.confirm_password')}
          value={values?.newPassConfirm}
          onChange={handleChange}
          error={errors.newPassConfirm}
          isInvalid={touched.newPassConfirm && !!errors.newPassConfirm}
          qaElement="new-password-confirm"
          qaElementFeedback="new-password-confirm-error"
        />
      </div>
      <div>
        <Button
          className="w-100 mb-4xl"
          disabled={loading}
          type="submit"
          size="lg"
          form="account-form"
          variant="primary"
          qa-element="save"
        >
          {t('common.save')}
        </Button>
        <Button
          className="w-100"
          disabled={loading}
          variant="link-gray"
          size="lg"
          onClick={handleLogOut}
          qa-element="logout"
          prefixIcon={<ArrowLeft />}
        >
          {t('common.back_to_main')}
        </Button>
        {/* {!user.twoFaOn && (
          <p className="font-body-s">
            <TwoFAIcon className="me-1" />
            <span>{t('auth.suggest_two_fa')}</span>
          </p>
        )} */}
      </div>
    </form>
  );
};

export default ResetPasswordForm;
